/**
 * MemoryStorage.ts - In-memory storage adapter
 * 
 * CHANGE HISTORY:
 * - 251027_2101 danielbeer: Move from Actr to Aggregatr
 */
import { StorageAdapter } from './StorageAdapter';
import type { EntityData, QueryFilters } from '../types/AggregatrTypes';

export class MemoryStorage extends StorageAdapter {
  data: Map<string, Map<string, EntityData>>;
  initialized: boolean;

  constructor() {
    super();
    this.data = new Map(); // entityType -> Map(id -> data)
    this.initialized = false;
  }

  /**
   * Initialize storage
   */
  async initialize() {
    if (this.initialized) return;
    this.data = new Map();
    this.initialized = true;
  }

  /**
   * Get storage for entity type
   */
  private _getStorage(entityType: string): Map<string, EntityData> {
    if (!this.data.has(entityType)) {
      this.data.set(entityType, new Map());
    }
    return this.data.get(entityType)!;
  }

  /**
   * Store an entity
   */
  async set(entityType: string, id: string, data: EntityData): Promise<EntityData> {
    const storage = this._getStorage(entityType);
    const entity: EntityData = {
      ...data,
      id,
      _type: entityType,
      _createdAt: (data as any)._createdAt || Date.now(),
      _updatedAt: Date.now()
    };
    storage.set(id, entity);
    return entity;
  }

  /**
   * Retrieve an entity
   */
  async get(entityType: string, id: string): Promise<EntityData | null> {
    const storage = this._getStorage(entityType);
    return storage.get(id) || null;
  }

  /**
   * Delete an entity
   */
  async delete(entityType: string, id: string): Promise<boolean> {
    const storage = this._getStorage(entityType);
    return storage.delete(id);
  }

  /**
   * Query entities
   */
  async query(entityType: string, filters: QueryFilters = {}): Promise<EntityData[]> {
    const storage = this._getStorage(entityType);
    const entities = Array.from(storage.values());

    // Apply filters
    return entities.filter(entity => {
      return Object.entries(filters).every(([key, value]) => {
        if (typeof value === 'function') {
          return value(entity[key]);
        }
        return entity[key] === value;
      });
    });
  }

  /**
   * Get all entities of a type
   */
  async getAll(entityType: string): Promise<EntityData[]> {
    const storage = this._getStorage(entityType);
    return Array.from(storage.values());
  }

  /**
   * Clear all data
   */
  async clear(): Promise<void> {
    this.data.clear();
  }

  /**
   * Get statistics
   */
  getStats(): Record<string, number> {
    const stats: Record<string, number> = {};
    for (const [entityType, storage] of this.data.entries()) {
      stats[entityType] = storage.size;
    }
    return stats;
  }
}

export default MemoryStorage;
